/**
 * API `code` is OrganizationalObject.code_str (`#105 - Name`). The table
 * already renders `name` on the next line, so keep only `#105` (ER-10958).
 * Bare values (`14`, `#1015`) are left unchanged.
 * @param {string|number|null|undefined} code
 * @returns {string}
 */
export function formatPositionCode(code) {
  if (code == null || code === "") return "";
  const match = String(code).match(/^#\d+/);
  return match ? match[0] : String(code);
}

/**
 * Maps a PositionSubscriptionRowSerializer payload (backend, snake_case) →
 * PositionsWithPayRangeSubscriptionsTable's PositionSubscriptionRow contract
 * (camelCase). Same mapper for mock and real data — positionPayRangeSubscriptionAPI.js
 * returns mock rows in the same raw shape the real endpoint does.
 *
 * Row contract: { id, code, name, department, occupant, subscription,
 * previousSubscriptionEffectiveTo, nextSubscriptionEffectiveFrom }. The last
 * two back the Rule 4 subscription-navigation chevrons — each is null when no
 * earlier/later subscription exists for the position. The mapped subscription
 * also carries previousVersionEffectiveTo / nextVersionEffectiveFrom (Rule 5
 * version-navigation boundaries), null when no earlier/later pay_range_version
 * exists on that subscription.
 * @param {object} apiRow
 * @returns {object}
 */
export function mapPositionSubscriptionRow(apiRow = {}) {
  return {
    id: apiRow.id,
    code: formatPositionCode(apiRow.code),
    name: apiRow.name,
    department: apiRow.department || "",
    occupant: mapOccupant(apiRow.occupant),
    subscription: mapSubscription(apiRow.subscription),
    previousSubscriptionEffectiveTo: apiRow.previous_subscription_effective_to || null,
    nextSubscriptionEffectiveFrom: apiRow.next_subscription_effective_from || null,
  };
}

function mapOccupant(occupant) {
  if (!occupant) return null;
  return {
    id: occupant.id,
    firstName: occupant.first_name,
    lastName: occupant.last_name,
    avatarUrl: occupant.avatar || null,
  };
}

/**
 * Maps a single subscription payload (backend, snake_case) → camelCase.
 * Exported so callers can map a position's raw subscription-history list
 * (@components/PositionsPayRangesPage/composables) without duplicating this shape.
 * previousVersionEffectiveTo / nextVersionEffectiveFrom back the card's Rule 5
 * version-navigation chevrons — each is null when no earlier/later
 * pay_range_version exists on this subscription.
 *
 * --- ER-11711 backend handoff (Position Fair Pay; do not implement in FE) ---
 * Ticket: open-ended subscription must show all pay range versions + coverage gaps.
 * `pay_range_version` FK = version at subscribe time (provenance only). Display must
 * resolve the pay range's full version chain over the subscription window at read time.
 *
 * Add to each subscription object in serialize_subscription() (keep flat version_*):
 * - versions: [{ version_number, effective_from, effective_to|null, min_amount, max_amount }]
 *   Every non-voided PayRangeVersion on that pay range overlapping [sub_from, sub_to],
 *   oldest first, each clipped DOWN to the subscription window (never extend end).
 * - coverage_gaps: [{ from, to|null }] spans inside the subscription with no version.
 *   Do not paper over gaps; log when coverage_gaps is non-empty; do not block writes.
 *
 * Prefetch (N+1): pay_range_version__pay_range__versions on
 * PositionPayRangeSubscriptionViewSet.list (+ subscribe/end reload), and on
 * list_positions_with_subscriptions covering/all_live prefetches (ER-11714).
 * Flag: idea-MDP-429. Tests: _versions_in_window / _coverage_gaps cases in ticket.
 * --- end ER-11711 backend handoff ---
 *
 * @param {object|null} subscription
 * @returns {object|null}
 */
export function mapSubscription(subscription) {
  if (!subscription) return null;
  return {
    id: subscription.id,
    payRangeId: subscription.pay_range_id,
    payRangeCode: subscription.pay_range_code,
    payRangeName: subscription.pay_range_name,
    status: subscription.status || "",
    countryCode: subscription.country_code || "",
    currencyCode: subscription.currency_code || "",
    payFrequency: subscription.pay_frequency || "",
    locationName: subscription.location_name || "",
    versionNumber: subscription.version_number ?? null,
    versionEffectiveFrom: subscription.version_effective_from || null,
    versionEffectiveTo: subscription.version_effective_to || null,
    previousVersionEffectiveTo: subscription.previous_version_effective_to || null,
    nextVersionEffectiveFrom: subscription.next_version_effective_from || null,
    minAmount: subscription.min_amount,
    maxAmount: subscription.max_amount,
    effectiveFrom: subscription.effective_from,
    effectiveTo: subscription.effective_to,
    readiness: {
      min: subscription.readiness?.min,
      at50: subscription.readiness?.at50,
      at100: subscription.readiness?.at100,
      max: subscription.readiness?.max,
    },
    versions: Array.isArray(subscription.versions)
      ? subscription.versions.map((version) => ({
          versionNumber: version.version_number,
          effectiveFrom: version.effective_from,
          effectiveTo: version.effective_to,
          minAmount: version.min_amount,
          maxAmount: version.max_amount,
        }))
      : [],
    coverageGaps: Array.isArray(subscription.coverage_gaps)
      ? subscription.coverage_gaps.map((gap) => ({
          from: gap.from,
          to: gap.to,
        }))
      : [],
  };
}
